import { translateObject } from '@jsverse/transloco';
import moment from 'moment';
import { DropdownObject } from '@inmoment/the-kitchen';

import {
  ActionItem,
  ActionPlan,
  ActionTask,
  Status,
} from 'app/providers/storage-api/actionplan.service';

interface StatusTranslations {
  not_started: string;
  in_progress: string;
  completed: string;
}

function getStatusTranslations(): StatusTranslations {
  return translateObject<StatusTranslations>('action-plan-utils.statuses') as StatusTranslations;
}

export function getStatusDs(): DropdownObject[] {
  const statusTranslations = getStatusTranslations();

  const statusDs = [
    { id: Status.NEW, title: statusTranslations.not_started },
    { id: Status.IN_PROGRESS, title: statusTranslations.in_progress },
    { id: Status.COMPLETED, title: statusTranslations.completed },
  ];

  return statusDs;
}

export function getStatusTranslation(status: Status): string {
  const statusTranslations = getStatusTranslations();

  switch (status) {
    default:
    case Status.NEW:
      return statusTranslations.not_started;
    case Status.IN_PROGRESS:
      return statusTranslations.in_progress;
    case Status.COMPLETED:
      return statusTranslations.completed;
  }
}

export function getMinDueDate(): Date {
  const date = new Date();

  date.setDate(date.getDate() - 1);

  return date;
}

export class ActionPlanUtils {
  public static isExpired(item: ActionPlan | ActionItem | ActionTask): boolean {
    return item.status !== 'COMPLETED' && this.getDateDiffInDays(item.due_date) >= 0;
  }

  public static getExpiredLabel(item: ActionPlan | ActionItem | ActionTask): string {
    const dateDiffInDays = this.getDateDiffInDays(item.due_date);

    if (item.status === 'COMPLETED') {
      if (dateDiffInDays > 0) {
        return `was ${dateDiffInDays} days ago`;
      } else if (dateDiffInDays < 0) {
        return `in ${-dateDiffInDays} days`;
      } else {
        return 'today';
      }
    }

    if (dateDiffInDays > 0) {
      return `expired ${dateDiffInDays} days ago`;
    } else if (dateDiffInDays < 0) {
      return `expires in ${-dateDiffInDays} days`;
    } else {
      return 'expires today';
    }
  }

  private static getDateDiffInDays(dueDate: string): number {
    return moment().startOf('day').diff(moment(dueDate).startOf('day'), 'days');
  }

  public static setChildCountsForPlan(actionPlan: ActionPlan): void {
    this.setChildCounts(actionPlan, actionPlan.action_items ?? []);
  }

  public static setChildCountsForItem(actionItem: ActionItem): void {
    this.setChildCounts(actionItem, actionItem.action_tasks ?? []);
  }

  private static setChildCounts(
    object: ActionPlan | ActionItem,
    childList: ActionItem[] | ActionTask[]
  ): void {
    let total = 0;
    let notStarted = 0;
    let inProgress = 0;
    let completed = 0;
    let cancelled = 0;
    let pastDue = 0;

    childList.forEach((c: ActionItem | ActionTask) => {
      ++total;

      switch (c.status) {
        case 'NEW':
          ++notStarted;
          break;
        case 'IN_PROGRESS':
          ++inProgress;
          break;
        case 'COMPLETED':
          ++completed;
          break;
        case 'CANCELLED':
          ++cancelled;
          break;
      }

      pastDue += this.isExpired(c) ? 1 : 0;
    });

    object.total_child_count = total;
    object.new_child_count = notStarted;
    object.in_progress_child_count = inProgress;
    object.completed_child_count = completed;
    object.cancelled_child_count = cancelled;
    object.past_due_child_count = pastDue;
  }
}
